/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import {
  CloseOutlined,
  ExportOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { Modal } from 'antd'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { paragraphXSmall, weightSemiBold } from 'src/components/css'
import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import { MODAL_TOKENS } from 'src/styles/modalTokens'
import { TITLE_LETTER_SPACING } from '../constants'

/** How long to wait before suggesting the new-tab fallback. */
const SLOW_LOAD_MS = 6000

type SportsPayAppLinkModalProps = {
  isOpen: boolean
  onClose: () => void
  /**
   * The application URL. Must already be validated with
   * `resolveSportsPayAppLink` — this component does not re-check it.
   */
  appLink: string
  /**
   * True while the caller is recording the completed registration. Holds the
   * frame open behind an overlay so the modal does not vanish mid-request.
   */
  isFinalizing?: boolean
}

/**
 * Embeds the SportsPay hosted application.
 *
 * Stacked ABOVE the signup modal rather than replacing its body, so closing
 * this returns to the application's detail view.
 *
 * The "Open in New Tab" link is not a nicety — it is the only recovery path.
 * SportsPay may serve `X-Frame-Options`/`frame-ancestors` headers that make the
 * iframe render blank, and a blocked frame is indistinguishable from a slow one
 * from JS: `onLoad` does not fire reliably, and cross-origin means the document
 * cannot be inspected. So the escape hatch is always visible rather than shown
 * on a failure we cannot detect.
 *
 * A loading state covers the wait, which can run to ~10s. `onLoad` DOES fire
 * for a cross-origin frame — only reading its document is blocked — so it is a
 * reliable signal that something rendered. What it cannot tell us is that the
 * frame was REFUSED: a blocked frame simply never fires. That is why the
 * spinner escalates to a "taking longer than usual" hint pointing at the
 * new-tab link, rather than spinning forever on a load that will never come.
 */
const SportsPayAppLinkModal: React.FC<SportsPayAppLinkModalProps> = ({
  isOpen,
  onClose,
  appLink,
  isFinalizing,
}) => {
  'use memo'

  const { width } = useWindowSize()
  const isMobile = width <= MODAL_TOKENS.MOBILE_BREAKPOINT
  const [isLoading, setIsLoading] = useState(true)
  const [isSlow, setIsSlow] = useState(false)
  /**
   * Distinguishes the modal opening from React reusing this component for a
   * different link, so the loader resets in both cases.
   */
  const loadedLinkRef = useRef<string | null>(null)

  const handleLoaded = useCallback(() => {
    loadedLinkRef.current = appLink
    setIsLoading(false)
    setIsSlow(false)
  }, [appLink])

  /**
   * Reset on every open, and whenever the link changes.
   *
   * Closing MUST clear `loadedLinkRef`: the modal is `destroyOnClose`, so the
   * iframe unmounts and reloads from scratch next time while this component
   * stays mounted. Without the clear, reopening the same link would remember it
   * as already loaded and skip the overlay — putting the white box straight
   * back.
   */
  useEffect(() => {
    if (!isOpen) {
      loadedLinkRef.current = null
      return
    }
    if (loadedLinkRef.current !== appLink) {
      setIsLoading(true)
      setIsSlow(false)
    }
  }, [isOpen, appLink])

  /**
   * After SLOW_LOAD_MS the copy changes to point at the new-tab link. This is
   * the only recourse for a frame SportsPay refused to serve — that case never
   * fires `onLoad`, so a timer is the only way to stop showing a bare spinner.
   */
  useEffect(() => {
    if (!isOpen || !isLoading) {
      return
    }
    const timer = setTimeout(() => setIsSlow(true), SLOW_LOAD_MS)
    return () => clearTimeout(timer)
  }, [isOpen, isLoading])

  const modalStyles = useMemo(
    () => ({
      body: {
        padding: 0,
        height: isMobile ? '100vh' : '90vh',
        maxHeight: isMobile ? '100vh' : '1080px',
        overflow: 'hidden',
      },
      content: {
        backgroundColor: colors.SECONDARY_300,
        padding: 0,
        height: isMobile ? '100vh' : '90vh',
        maxHeight: isMobile ? '100vh' : '1080px',
      },
      // Sits one level above the signup modal that opened it, so it is not
      // trapped behind its own parent.
      wrapper: { zIndex: MODAL_TOKENS.Z_INDEX_WRAPPER + 2 },
      mask: {
        backdropFilter: `blur(${MODAL_TOKENS.MASK_BLUR})`,
        backgroundColor: MODAL_TOKENS.MASK_COLOR,
        zIndex: MODAL_TOKENS.Z_INDEX_MASK + 2,
      },
    }),
    [isMobile]
  )

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      closeIcon={null}
      width={isMobile ? MODAL_TOKENS.WIDTH_MOBILE : '90vw'}
      style={{ maxWidth: '1400px' }}
      styles={modalStyles}
      centered
      // The iframe holds the user's in-progress application; keep it mounted
      // only while open so reopening always starts from a fresh load.
      destroyOnClose
    >
      <div css={layoutStyle}>
        <div css={headerStyle}>
          <Text
            variant='paragraphSmall'
            weight='semiBold'
            color={colors.NEUTRAL_0}
            style={TITLE_LETTER_SPACING}
          >
            {capitalizeWords('SportsPay Application')}
          </Text>

          <div css={headerActionsStyle}>
            <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
              Not loading?
            </Text>
            <a
              href={appLink}
              target='_blank'
              rel='noopener noreferrer'
              css={newTabStyle}
            >
              <ExportOutlined css={newTabIconStyle} />
              {capitalizeWords('Open in New Tab')}
            </a>
            <button
              type='button'
              /*
               * Closing tears this modal down, so the click must not continue
               * on to ancestors that are about to be re-rendered underneath it.
               */
              onClick={(event) => {
                event.stopPropagation()
                onClose()
              }}
              css={closeButtonStyle}
              aria-label='Close application'
            >
              <CloseOutlined />
            </button>
          </div>
        </div>

        <div css={frameWrapStyle}>
          {isFinalizing ? (
            <div css={loadingStyle} role='status' aria-live='polite'>
              <LoadingOutlined css={spinnerStyle} spin />
              <Text variant='paragraphSmall' color={colors.NEUTRAL_0}>
                {capitalizeWords('Finalizing Your Registration')}
              </Text>
            </div>
          ) : null}

          {isLoading && !isFinalizing ? (
            <div css={loadingStyle} role='status' aria-live='polite'>
              <LoadingOutlined css={spinnerStyle} spin />
              <Text variant='paragraphSmall' color={colors.NEUTRAL_0}>
                {isSlow
                  ? 'Still loading…'
                  : capitalizeWords('Loading Your Application')}
              </Text>
              {isSlow ? (
                <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
                  This is taking longer than usual. You can open it in a new tab
                  instead.
                </Text>
              ) : null}
            </div>
          ) : null}

          <iframe
            src={appLink}
            title='SportsPay Application'
            onLoad={handleLoaded}
            css={[frameStyle, isLoading && hiddenFrameStyle]}
            /* Enough to run their form and let it navigate/pop out, without
               granting top-level navigation that is not user-initiated. */
            sandbox='allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation'
            referrerPolicy='no-referrer-when-downgrade'
          />
        </div>
      </div>
    </Modal>
  )
}

export default SportsPayAppLinkModal

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

const layoutStyle = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
`

const headerStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  flex-shrink: 0;
  padding: 12px 16px;
  border-bottom: 1px solid ${colors.SECONDARY_100};
`

const headerActionsStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
`

const newTabIconStyle = css`
  font-size: 12px;
`

const newTabStyle = css`
  ${paragraphXSmall}
  ${weightSemiBold}
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${colors.PRIMARY};
  text-decoration: underline;
  white-space: nowrap;
  &:hover {
    opacity: 0.85;
  }
`

const closeButtonStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: ${colors.SOLITUDE};
  cursor: pointer;
  transition: color 0.2s;
  &:hover {
    color: ${colors.WHITE};
  }
`

/**
 * Holds the frame and its loading overlay in one stacking context.
 *
 * `flex: 1` with `min-height: 0` so it fills the remaining height instead of
 * collapsing to its intrinsic size inside a flex column. The dark background
 * matters: it is what shows during the load, in place of the white flash the raw
 * iframe would give.
 */
const frameWrapStyle = css`
  position: relative;
  flex: 1;
  min-height: 0;
  background: ${colors.SECONDARY_300};
`

const loadingStyle = css`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  text-align: center;
  background: ${colors.SECONDARY_300};
`

const spinnerStyle = css`
  font-size: 28px;
  color: ${colors.PRIMARY};
`

const frameStyle = css`
  width: 100%;
  height: 100%;
  border: none;
  background: ${colors.WHITE};
`

/**
 * Hides the frame WITHOUT unmounting it — it has to keep loading underneath the
 * overlay, and `display: none` in some engines defers or restarts that. Opacity
 * keeps it live while the overlay covers the white page it paints first.
 */
const hiddenFrameStyle = css`
  opacity: 0;
`
